import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { updateUserData, setUserAvatar, UserData } from '../redux/slices/userSlice';
import LeadStatusIndicator from '../components/LeadStatusIndicator';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Badge,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Chat as ChatIcon,
  Add as AddIcon,
} from '@mui/icons-material';

interface EditDataState extends UserData {
  currentNote?: string;
}

type SnackbarSeverity = 'success' | 'error' | 'warning';

const ProfilePage: React.FC = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.user.data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(userData);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as SnackbarSeverity
  });
  const [isAddingInteraction, setIsAddingInteraction] = useState(false);
  const [newInteraction, setNewInteraction] = useState({
    type: 'email' as const,
    description: '',
    leadId: '',
  });

  const handleEditClick = () => {
    setEditData(userData);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData(userData);
  };

  const handleSaveEdit = () => {
    try {
      dispatch(updateUserData(editData));
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Perfil atualizado com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar o perfil. Tente novamente.',
        severity: 'error'
      });
    }
  };

  const handleChange = (field: keyof UserData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditData({
      ...editData,
      [field]: event.target.value
    });
  };

  const handleSaveNote = () => {
    if (!currentNote.trim()) {
      setSnackbar({
        open: true,
        message: 'Por favor, adicione algum texto à sua anotação.',
        severity: 'warning'
      });
      return;
    }

    const newNote = {
      text: currentNote.trim(),
      createdAt: new Date().toISOString()
    };

    const updatedNotes = [...(userData.notes || []), newNote];
    
    dispatch(updateUserData({
      ...userData,
      notes: updatedNotes
    }));

    setCurrentNote('');
    setIsEditingNotes(false);
    setSnackbar({
      open: true,
      message: 'Anotação salva com sucesso!',
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newAvatar = reader.result as string;
        dispatch(setUserAvatar(newAvatar));
        setSnackbar({
          open: true,
          message: 'Foto de perfil atualizada com sucesso!',
          severity: 'success'
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteNote = (createdAt: string) => {
    const updatedNotes = userData.notes.filter(note => note.createdAt !== createdAt);
    
    dispatch(updateUserData({
      ...userData,
      notes: updatedNotes
    }));

    setSnackbar({
      open: true,
      message: 'Anotação excluída com sucesso!',
      severity: 'success'
    });
  };

  const handleAddInteraction = () => {
    if (!newInteraction.description.trim()) {
      setSnackbar({
        open: true,
        message: 'Por favor, adicione uma descrição à interação.',
        severity: 'warning'
      });
      return;
    }

    const interaction = {
      ...newInteraction,
      date: new Date().toISOString()
    };

    const updatedInteractions = [...(userData.interactions || []), interaction];
    
    dispatch(updateUserData({
      ...userData,
      interactions: updatedInteractions
    }));

    setNewInteraction({
      type: 'email',
      description: '',
      leadId: '',
    });
    setIsAddingInteraction(false);
    setSnackbar({
      open: true,
      message: 'Interação registrada com sucesso!',
      severity: 'success'
    });
  };

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <EmailIcon />;
      case 'whatsapp':
        return <WhatsAppIcon />;
      case 'phone':
        return <PhoneIcon />;
      case 'chat':
        return <ChatIcon />;
      default:
        return <ChatIcon />;
    }
  };

  const getInteractionColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'primary';
      case 'whatsapp':
        return 'success';
      case 'phone':
        return 'info';
      case 'chat':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {/* Informações Principais */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <IconButton
                        onClick={handlePhotoClick}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          width: 32,
                          height: 32,
                        }}
                      >
                        <PhotoCameraIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    }
                  >
                    <Avatar
                      src={userData.avatar || undefined}
                      sx={{
                        width: 120,
                        height: 120,
                        margin: '0 auto 16px',
                        bgcolor: 'primary.main',
                        cursor: 'pointer',
                      }}
                      onClick={handlePhotoClick}
                    >
                      {userData.name.charAt(0)}
                    </Avatar>
                  </Badge>
                </Box>
                {isEditing ? (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Nome"
                      value={editData.name}
                      onChange={handleChange('name')}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      label="Cargo"
                      value={editData.role}
                      onChange={handleChange('role')}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                ) : (
                  <>
                    <Typography variant="h5" gutterBottom>
                      {userData.name}
                    </Typography>
                    <Typography color="textSecondary" gutterBottom>
                      {userData.role}
                    </Typography>
                  </>
                )}
                {isEditing ? (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveEdit}
                      sx={{ mr: 1 }}
                    >
                      Salvar
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEdit}
                    >
                      Cancelar
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                    sx={{ mt: 2 }}
                  >
                    Editar Perfil
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Detalhes do Contato */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informações de Contato
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon color="primary" />
                    </ListItemIcon>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Nome Completo"
                        value={editData.name}
                        onChange={handleChange('name')}
                      />
                    ) : (
                      <ListItemText
                        primary="Nome Completo"
                        secondary={userData.name}
                      />
                    )}
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="E-mail"
                        value={editData.email}
                        onChange={handleChange('email')}
                      />
                    ) : (
                      <ListItemText
                        primary="E-mail"
                        secondary={userData.email}
                      />
                    )}
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Telefone"
                        value={editData.phone}
                        onChange={handleChange('phone')}
                      />
                    ) : (
                      <ListItemText
                        primary="Telefone"
                        secondary={userData.phone}
                      />
                    )}
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BusinessIcon color="primary" />
                    </ListItemIcon>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Empresa"
                        value={editData.company}
                        onChange={handleChange('company')}
                      />
                    ) : (
                      <ListItemText
                        primary="Empresa"
                        secondary={userData.company}
                      />
                    )}
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon color="primary" />
                    </ListItemIcon>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Plano"
                        value={editData.plan}
                        onChange={handleChange('plan')}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <ListItemText primary="Plano" />
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                            textTransform: 'none',
                            px: 2,
                            ml: 2
                          }}
                        >
                          {userData.plan}
                        </Button>
                      </Box>
                    )}
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Card de Anotações */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Anotações
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={isEditingNotes ? <SaveIcon /> : <EditIcon />}
                    onClick={() => {
                      if (isEditingNotes) {
                        handleSaveNote();
                      } else {
                        setIsEditingNotes(true);
                      }
                    }}
                  >
                    {isEditingNotes ? 'Salvar' : 'Nova Anotação'}
                  </Button>
                </Box>
                
                {isEditingNotes ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={currentNote}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    placeholder="Adicione sua anotação aqui..."
                    variant="outlined"
                  />
                ) : (
                  <Box>
                    {userData.notes && userData.notes.length > 0 ? (
                      userData.notes.map((note, index) => (
                        <Box 
                          key={note.createdAt} 
                          sx={{ 
                            mb: 2, 
                            p: 2, 
                            bgcolor: '#f5f5f5', 
                            borderRadius: 1,
                            '&:last-child': { mb: 0 },
                            position: 'relative'
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteNote(note.createdAt)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              color: 'error.main',
                              '&:hover': {
                                bgcolor: 'error.light',
                                color: 'error.contrastText'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Typography 
                            variant="caption" 
                            color="textSecondary" 
                            sx={{ display: 'block', mb: 1 }}
                          >
                            {new Date(note.createdAt).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {note.text}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        Nenhuma anotação adicionada.
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Seção de Interações */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Interações
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setIsAddingInteraction(true)}
                    variant="outlined"
                    color="primary"
                  >
                    Nova Interação
                  </Button>
                </Box>

                {userData.interactions && userData.interactions.length > 0 ? (
                  <List>
                    {userData.interactions.map((interaction, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            {getInteractionIcon(interaction.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                  label={interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)}
                                  color={getInteractionColor(interaction.type) as any}
                                  size="small"
                                />
                                <Typography variant="body2" color="textSecondary">
                                  {new Date(interaction.date).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                            secondary={interaction.description}
                          />
                        </ListItem>
                        {index < userData.interactions.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography color="textSecondary" align="center">
                    Nenhuma interação registrada
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Estatísticas */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Desempenho
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="primary">
                        {userData.stats.totalSales}
                      </Typography>
                      <Typography color="textSecondary">
                        Vendas Totais
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="primary">
                        {userData.stats.activeLeads}
                      </Typography>
                      <Typography color="textSecondary">
                        Leads Ativos
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="primary">
                        {userData.stats.completedDeals}
                      </Typography>
                      <Typography color="textSecondary">
                        Negócios Fechados
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Dialog para Nova Interação */}
      <Dialog
        open={isAddingInteraction}
        onClose={() => setIsAddingInteraction(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nova Interação</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Tipo de Interação</InputLabel>
              <Select
                value={newInteraction.type}
                label="Tipo de Interação"
                onChange={(e) => setNewInteraction({ ...newInteraction, type: e.target.value as any })}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="phone">Telefone</MenuItem>
                <MenuItem value="chat">Chat</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Descrição"
              value={newInteraction.description}
              onChange={(e) => setNewInteraction({ ...newInteraction, description: e.target.value })}
              margin="normal"
              multiline
              rows={4}
              required
            />
            <TextField
              fullWidth
              label="ID do Lead (opcional)"
              value={newInteraction.leadId}
              onChange={(e) => setNewInteraction({ ...newInteraction, leadId: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddingInteraction(false)}>
            Cancelar
          </Button>
          <Button onClick={handleAddInteraction} variant="contained" color="primary">
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage; 